export function readAlertMetadata(value) {
  if (typeof value === "string") {
    try {
      return readAlertMetadata(JSON.parse(value));
    } catch {
      return {};
    }
  }
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}
const integer = (value) =>
  value !== null && value !== undefined && value !== "" && Number.isInteger(Number(value))
    ? Number(value)
    : null;
const text = (value) =>
  typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
export function formatAlertDate(value) {
  const date = new Date(value);
  return value && Number.isFinite(date.getTime())
    ? date.toLocaleString("es-CO", {
        timeZone: "America/Bogota",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      })
    : "Sin fecha";
}
export function normalizeAlert(alert) {
  if (!alert || typeof alert !== "object" || Array.isArray(alert)) return null;
  if (
    !["id", "alert_type", "metadata", "event_id", "host", "reader_name", "equipment"].some(
      (key) => alert[key] != null
    )
  )
    return null;
  const metadata = readAlertMetadata(alert.metadata);
  const host = metadata.host || {};
  const hosts = Array.isArray(metadata.hosts) ? metadata.hosts : [];
  const equipment =
    text(host.name || hosts[0]?.host || alert.reader_name || alert.equipment || alert.host) ||
    "Equipo sin información";
  const commerce =
    text(alert.commerce || metadata.commerce) ||
    equipment.split(/\s+-\s+(?:READER|RASPBERRY|C[AÁ]MARA|ENTRADA|SALIDA)\b/i)[0];
  const interfaces = Array.isArray(host.interfaces) ? host.interfaces : [];
  const ip =
    text(
      alert.ip || interfaces.find((item) => String(item.main) === "1")?.ip || interfaces[0]?.ip
    ) || "Sin IP";
  const message =
    text(
      alert.msm ||
        alert.message ||
        metadata.event?.name ||
        metadata.problem?.name ||
        metadata.opdata ||
        metadata.name
    ) || "Alerta sin descripción";
  const priority = integer(alert.priority ?? metadata.problem?.severity ?? metadata.severity);
  const severity = priority >= 4 ? "error" : priority >= 2 ? "warning" : "info";
  const labels = {
    0: "Desconocida",
    1: "Información",
    2: "Advertencia",
    3: "Alta",
    4: "Crítica",
    5: "Emergencia",
  };
  let status = integer(alert.status);
  if (status === null && metadata.problem?.r_eventid && metadata.problem.r_eventid !== "0")
    status = 1;
  // metadata.hosts[].status describes the host; it does not resolve an alert.
  const clock = Number(metadata.problem?.clock ?? metadata.event?.clock ?? metadata.clock);
  const dateValue =
    alert.createdAt ||
    alert.timestamp ||
    (clock > 0 && Number.isFinite(clock) ? new Date(clock * 1000).toISOString() : null);
  const timestamp = dateValue && Number.isFinite(Date.parse(dateValue)) ? dateValue : null;
  const eventId =
    text(
      alert.event_id ||
        metadata.event?.eventid ||
        metadata.problem?.eventid ||
        metadata.eventid ||
        alert.id
    ) || `${equipment}|${message}|${timestamp || "undated"}`;
  const notified =
    alert.sendalert === true ||
    alert.sendalert === 1 ||
    alert.sendalert === "1" ||
    alert.sendalert === "true"
      ? true
      : [false, 0, "0", "false"].includes(alert.sendalert)
      ? false
      : null;
  return {
    id: eventId,
    eventId,
    recordId: alert.id ?? null,
    time: formatAlertDate(timestamp),
    timestamp,
    message,
    equipment,
    commerce: /^\d+$/.test(commerce) ? "Comercio sin información" : commerce,
    hostId: text(alert.host || hosts[0]?.hostid || host.hostid),
    ip,
    priority,
    priorityText:
      labels[priority] || (priority === null ? "Sin prioridad" : `Prioridad ${priority}`),
    severity,
    status,
    statusText: status === 0 ? "Activa" : status === 1 ? "Resuelta" : "Sin estado",
    sendAlert: notified,
    notificationText:
      notified === true ? "Enviada" : notified === false ? "No enviada" : "Sin información",
    metadataSeverity: metadata.severity ?? metadata.problem?.severity ?? null,
    alerts: { alert_general: message },
    metadata,
    rawData: alert,
    read: status === 1,
    alertType: text(alert.alert_type) || "Sin tipo",
  };
}
export function sortAlerts(alerts) {
  return [...alerts].sort(
    (a, b) => (Date.parse(b.timestamp) || 0) - (Date.parse(a.timestamp) || 0)
  );
}
export function filterAlerts(
  alerts,
  { severity = "all", status = "all", type = "all", search = "" } = {}
) {
  const severityKey = { critical: "error", warning: "warning", info: "info" }[severity];
  const query = search
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return alerts.filter(
    (alert) =>
      (!severityKey || alert.severity === severityKey) &&
      (status === "all" ||
        (status === "unknown"
          ? ![0, 1].includes(alert.status)
          : String(alert.status) === status)) &&
      (type === "all" || alert.alertType === type) &&
      (!query ||
        [
          alert.recordId,
          alert.eventId,
          alert.hostId,
          alert.message,
          alert.commerce,
          alert.equipment,
          alert.ip,
        ].some((value) =>
          String(value || "")
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .toLowerCase()
            .includes(query)
        ))
  );
}
export function alertStats(alerts) {
  return {
    total: alerts.length,
    active: alerts.filter((a) => a.status === 0).length,
    resolved: alerts.filter((a) => a.status === 1).length,
    critical: alerts.filter((a) => a.severity === "error").length,
    warning: alerts.filter((a) => a.severity === "warning").length,
    info: alerts.filter((a) => a.severity === "info").length,
  };
}
export function socketAlertItems(value) {
  if (typeof value === "string") {
    try {
      return socketAlertItems(JSON.parse(value));
    } catch {
      return [];
    }
  }
  if (Array.isArray(value)) return value.flatMap(socketAlertItems);
  if (!value || typeof value !== "object") return [];
  if (
    value.data != null &&
    !["msm", "message", "alert_type", "metadata", "event_id", "host"].some(
      (key) => value[key] != null && key !== "message"
    )
  )
    return socketAlertItems(value.data);
  const normalized = normalizeAlert(value);
  return normalized ? [normalized] : [];
}
